import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Paper,
    Alert as MuiAlert,
    useTheme,
    Button,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const NewsletterProgress = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { nid } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('pending');
    const [title, setTitle] = useState('');
    const [error, setError] = useState('');
    const [currentStage, setCurrentStage] = useState('');
    // 'competition' | 'admission' — drives the back-nav so the user lands on
    // the right tab when leaving this page. Defaults to competition (the
    // historical tab) until the first poll returns the actual type.
    const [newsletterType, setNewsletterType] = useState('competition');

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const getStageFromProgress = (prog) => {
        if (prog < 20) return 'Gathering competition data...';
        if (prog < 50) return 'Generating content with AI...';
        if (prog < 80) return 'Creating themed images with AI...';
        if (prog < 90) return 'Generating HTML newsletter...';
        if (prog < 100) return 'Finalizing and saving...';
        return 'Complete!';
    };

    const getActiveStep = (prog) => {
        if (prog < 20) return 0;
        if (prog < 50) return 1;
        if (prog < 80) return 2;
        if (prog < 90) return 3;
        if (prog < 100) return 4;
        return 5;
    };

    const steps = [
        'Gathering Data',
        'AI Content Generation',
        'Image Generation',
        'HTML Generation',
        'Saving',
    ];

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Newsletters');

        let interval;

        const pollStatus = async () => {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/status`,
                    { headers: { Authorization: getAuthHeader() } }
                );

                setProgress(response.data.progress || 0);
                setStatus(response.data.status);
                setTitle(response.data.title || '');
                if (response.data.type) setNewsletterType(response.data.type);
                setCurrentStage(getStageFromProgress(response.data.progress || 0));

                if (response.data.status === 'completed') {
                    clearInterval(interval);
                    setTimeout(() => {
                        navigate(`/admin/newsletters/${nid}`);
                    }, 2000);
                } else if (response.data.status === 'failed') {
                    clearInterval(interval);
                    setError(response.data.error_message || 'Newsletter generation failed');
                }
            } catch (err) {
                console.error('Polling error:', err);
                if (err.response?.status === 401) {
                    handleLogout();
                } else if (err.response?.status === 404) {
                    setError('Newsletter not found');
                    clearInterval(interval);
                }
            }
        };

        // Initial call
        pollStatus();

        // Poll every 5 seconds
        interval = setInterval(pollStatus, 5000);

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [nid]);

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title="Newsletter Generation Progress" subtitle={title || 'Generating Newsletter'} />

            <Paper
                sx={{
                    p: 4,
                    maxWidth: 800,
                    mx: 'auto',
                    mt: 4,
                    backgroundColor: colors.primary[600]
                }}
            >
                {error ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <ErrorIcon sx={{ fontSize: 64, color: colors.redAccent[500] }} />
                        <Typography variant="h4" color="error">
                            Generation Failed
                        </Typography>
                        <MuiAlert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </MuiAlert>
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/admin/newsletters/${newsletterType}`)}
                            sx={{ mt: 2 }}
                        >
                            Back to Newsletters
                        </Button>
                    </Box>
                ) : status === 'completed' ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: colors.greenAccent[500] }} />
                        <Typography variant="h4" color={colors.greenAccent[500]}>
                            Newsletter Generated Successfully!
                        </Typography>
                        <Typography variant="body1" color={colors.grey[300]}>
                            Redirecting to newsletter viewer...
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h5" mb={2} color={colors.grey[100]}>
                            {currentStage}
                        </Typography>

                        <Box mb={4}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color={colors.grey[300]}>
                                    Progress
                                </Typography>
                                <Typography variant="body2" color={colors.grey[300]}>
                                    {progress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    backgroundColor: colors.primary[700],
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: colors.greenAccent[500]
                                    }
                                }}
                            />
                        </Box>

                        <Stepper activeStep={getActiveStep(progress)} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box
                            mt={4}
                            p={2}
                            sx={{
                                backgroundColor: colors.primary[700],
                                borderRadius: 1,
                                border: `1px solid ${colors.primary[500]}`
                            }}
                        >
                            <Typography variant="body2" color={colors.grey[300]}>
                                <strong>Status:</strong> {status.toUpperCase()}
                            </Typography>
                            <Typography variant="caption" color={colors.grey[400]} mt={1} display="block">
                                This process may take several minutes. Please do not close this page.
                            </Typography>
                        </Box>

                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/admin/newsletters/${newsletterType}`)}
                            >
                                View All Newsletters
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default NewsletterProgress;
